import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ItemDetailBoxFragmentDoc,
  ItemResaleRaffleUserDetailFragmentDoc,
  ResaleApplicationDetailQuery,
  ResaleApplicationDetailQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { decodeSkuId, setItemSku } from '@/core/sku'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } =
    useRouter<{ itemId: string; skuId?: string }>()
  const itemId = router.query.itemId
  const skuId = router.query?.skuId

  const { data, loading } = useQuery<
    ResaleApplicationDetailQuery,
    ResaleApplicationDetailQueryVariables
  >(QUERY, {
    variables: {
      id: itemId,
      itemId,
      userId: user?.uid ?? '',
      skuId: decodeSkuId(skuId ?? '') || undefined,
    },
  })

  const itemResaleRaffle = useMemo(() => {
    if (!data?.userActiveResaleItemRaffle) {
      return null
    }
    return {
      ...data.userActiveResaleItemRaffle,
      item: setItemSku(
        data.userActiveResaleItemRaffle?.item,
        data.userActiveResaleItemRaffle.skuId ?? skuId
      ),
    }
  }, [data?.userActiveResaleItemRaffle, skuId])

  const item = useMemo(() => {
    if (!data?.item) {
      return null
    }
    return setItemSku(
      data.item,
      data.userActiveResaleItemRaffle?.skuId ?? router.query.skuId
    )
  }, [data?.item, data?.userActiveResaleItemRaffle?.skuId, router.query.skuId])

  return {
    state: {
      loading,
      item,
      itemResaleRaffle,
    },
    actions: {
      onSubmit: () => {
        router.push({
          pathname: generatePath(
            routerPages.ResaleApplicationCreatePrecaution,
            {
              itemId,
            }
          ),
          search: `${skuId ? `?skuId=${skuId}` : ''}`,
        })
      },
    },
  }
})

const QUERY = gql`
  query ResaleApplicationDetail(
    $id: ID!
    $itemId: String!
    $userId: String
    $skuId: String
  ) {
    item(id: $id) {
      ...ItemDetailBox
    }
    userActiveResaleItemRaffle(
      itemId: $itemId
      skuId: $skuId
      userId: $userId
    ) {
      ...ItemResaleRaffleUserDetail
    }
  }
  ${ItemDetailBoxFragmentDoc}
  ${ItemResaleRaffleUserDetailFragmentDoc}
`
